import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import capitalize from '~/utils/string';
import { DUTCH_COUNTRIES_BY_ISO_CODE } from '~/constants';
import FormControl from '../common/form-control.component';
import Select from '../common/select.component';
import Spinner from '../common/spinner.component';

export interface SiteValues {
  name: string;
  address: string;
  postcode: string;
  city: string;
  isHeadquarters: boolean;
  country: string;
}

interface SiteFormProps {
  initialValues?: Partial<SiteValues>;
  onSubmit: (values: SiteValues) => Promise<void>;
  onCancel?: () => void;
  error?: string;
  isUpdate?: boolean;
}

const SiteForm = ({
  initialValues,
  onSubmit,
  onCancel,
  error,
  isUpdate,
}: SiteFormProps) => {
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    name: Yup.string().required(capitalize(t('verplicht veld'))),
    address: Yup.string().required(capitalize(t('verplicht veld'))),
    postcode: Yup.string().required(capitalize(t('verplicht veld'))),
    city: Yup.string().required(capitalize(t('verplicht veld'))),
    country: Yup.string().required(capitalize(t('verplicht veld'))),
    isHeadquarters: Yup.boolean().required(capitalize(t('verplicht veld'))),
  });

  return (
    <Formik
      initialValues={{
        name: '',
        address: '',
        postcode: '',
        city: '',
        isHeadquarters: false,
        ...initialValues,
        country: initialValues?.country || 'BE',
      }}
      isInitialValid={isUpdate}
      validationSchema={schema}
      onSubmit={(values) => onSubmit(values)}
    >
      {({
        values,
        handleChange,
        handleBlur,
        isSubmitting,
        handleSubmit,
        isValid,
        setFieldValue,
        dirty,
      }) => (
        <div className="w-full space-y-4">
          <FormControl name="name" label={capitalize(t('name'))} isRequired>
            <input
              id="name"
              name="name"
              type="name"
              className="c-input"
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </FormControl>

          <div className="grid grid-cols-2 gap-6">
            <FormControl
              name="address"
              label={capitalize(t('adres'))}
              isRequired
            >
              <input
                id="address"
                name="address"
                type="address"
                className="c-input"
                value={values.address}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormControl>

            <FormControl name="city" label={capitalize(t('plaats'))} isRequired>
              <input
                id="city"
                name="city"
                type="city"
                className="c-input"
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormControl>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <FormControl
              name="postcode"
              label={capitalize(t('postcode'))}
              isRequired
            >
              <input
                id="postcode"
                name="postcode"
                type="postcode"
                className="c-input"
                value={values.postcode}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </FormControl>

            <FormControl
              name="country"
              label={capitalize(t('land'))}
              isRequired
            >
              <Select
                value={{
                  value: values.country.toUpperCase(),
                  label:
                    DUTCH_COUNTRIES_BY_ISO_CODE[values.country.toUpperCase()],
                }}
                placeholder={capitalize(t('selecteer een land...'))}
                options={Object.keys(DUTCH_COUNTRIES_BY_ISO_CODE).reduce(
                  (result, isoCode) => [
                    ...result,
                    {
                      value: isoCode,
                      label: DUTCH_COUNTRIES_BY_ISO_CODE[isoCode],
                    },
                  ],
                  [],
                )}
                onBlur={handleBlur}
                onChange={(value) => {
                  setFieldValue('country', value.value);
                }}
              />
            </FormControl>
          </div>

          <FormControl name="isHeadquarters">
            <label
              className="flex space-x-2 items-center py-2"
              htmlFor="isHeadquarters"
            >
              <input
                id="isHeadquarters"
                name="isHeadquarters"
                type="checkbox"
                className="c-checkbox"
                onChange={handleChange}
                onBlur={handleBlur}
                checked={!!values.isHeadquarters}
              />
              <span className="text-xs">{capitalize(t('hoofdvestiging'))}</span>
            </label>
          </FormControl>

          <div className="flex justify-end">
            {onCancel && (
              <button
                className="c-button c-button--outline mr-2"
                type="button"
                onClick={onCancel}
              >
                {capitalize(t('annuleer'))}
              </button>
            )}
            <button
              className="c-button c-button--primary flex"
              type="button"
              onClick={() => handleSubmit()}
              disabled={isSubmitting || !isValid || !dirty}
            >
              {isSubmitting && <Spinner />}
              {capitalize(t('bewaar'))}
            </button>
          </div>
          {error && <p className="c-error mt-2">{error}</p>}
        </div>
      )}
    </Formik>
  );
};

export default SiteForm;
